import React, { useState } from "react";
import { Flex } from "rebass/styled-components";
import styled from "styled-components";
import { ClipCardDescription } from "./../../Components/ClipDescription";
import { ClipRemix } from "./../../Components/ClipRemix";
import { Link } from "../../../Button";
import { Avatar } from "../../../Avatar";
import { Headline } from "../../../Text";
import {
  AvatarBorder,
  getAvatarBorderLevel,
  getAvatarImage,
} from "../../../../Utilities/User";
import { Divider } from "../../../Divider";
import { canRemix, getDisplayName } from "../../../../Utilities/Clip";
import { EPaywallType } from "../../../Modals/Paywall";
import { useInteractonInterceptor } from "../../../../Hooks/useInteractionInterceptor";
import { trackClipDownload } from "../../../../../utils";
import { useUser } from "../../../../Hooks/useUser";
import { ClipReactions } from "../ClipReactions";
import { TClipControlsProps } from "../../@types";
import Remix from "../../../../../components/atoms/form/button/remix";
import { ClipOptions } from "../ClipOptions/Index";
import { useRecoilState } from "recoil";
import { AllstarModalState } from "../../../../State/modals";
import { ModalType } from "../../../../@types";

export const ClipControlsFeed = ({
  isFeed,
  clip,
  followUnfollow,
  setPaywallOpen,
  setRemixModalOpen,
  setDownloadModalOpen,
  setShareModalOpen,
  setReportModalOpen,
  setModalOpen,
  setEditModalOpen,
  setFollowModal,
  isProfile,
  isMontage,
  mutateReaction,
  hideDivider,
}: TClipControlsProps) => {
  const checkFeatureAccessQuery = useInteractonInterceptor();
  const { allstarUser } = useUser();
  const [clipDuration] = useState();
  const isOwnClip = allstarUser?.user?._id === clip.user._id;
  const ownerId = clip?.user?._id;
  const avatarBorderLevel = getAvatarBorderLevel(clip.user);
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const displayName = getDisplayName(clip);
  const avatar = getAvatarImage(clip.user);
  const to = isMontage
    ? `/montage?montage=${clip.shareId}`
    : `/clip?clip=${clip.shareId}`;

  return (
    <>
      <StyledContentColumn>
        <Flex width="100%" justifyContent="flex-start" flexDirection={"column"}>
          <ClipRemix
            views={clip.views}
            followUnfollow={followUnfollow}
            clip={clip}
            height="100%"
            setFollowModal={setFollowModal}
            onClick={undefined}
            classification={undefined}
            alignment={undefined}
          />
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            width="100%"
            mt={4}
            mb={3}
          >
            <ClipCardDescription
              to={to}
              clip={clip}
              height="100%"
              onClick={undefined}
              classification={undefined}
              truncate={undefined}
              alignment={undefined}
              isMontage={isMontage}
            />
            <Flex>
              {!isMontage && (
                <StyledClipReactions
                  justifyContent="flex-start"
                  clipId={clip?._id}
                  clip={clip}
                  mutateReaction={mutateReaction}
                />
              )}
              {canRemix({ ...clip, isMontage }, allstarUser?.isAdmin) && (
                <Remix
                  clip={clip}
                  classificiation={undefined}
                  onClick={undefined}
                  setModalOpen={setRemixModalOpen}
                  classification={undefined}
                  isFeed={isFeed}
                  disableOnClick={false}
                />
              )}
            </Flex>
          </Flex>

          <OwnerActions>
            <Owner
              mb={5}
              onClick={(e: any) => {
                e.stopPropagation();
              }}
            >
              {avatarBorderLevel ? (
                <AvatarBorder type={avatarBorderLevel} size="nav">
                  <Avatar
                    size="nav"
                    image={avatar}
                    to={`/profile?user=${ownerId}`}
                  />
                </AvatarBorder>
              ) : (
                <Avatar
                  size="nav"
                  image={avatar}
                  to={`/profile?user=${ownerId}`}
                />
              )}

              <OwnerLink
                flex={true}
                color="inherit"
                to={`/profile?user=${ownerId}`}
                name="clip-username"
              >
                <Headline ml="15px">{displayName}</Headline>
              </OwnerLink>
            </Owner>

            <ClipOptions
              clip={clip}
              isFeed={isFeed}
              clipLink={
                !isMontage
                  ? `https://${window.location.hostname}/clip?clip=${clip.shareId}`
                  : `https://${window.location.hostname}/montage?montage=${clip.shareId}`
              }
              duration={clipDuration}
              classification="secondaryRounded"
              isProfile={isProfile}
              isMontage={isMontage}
              isOwnClip={isOwnClip}
              setDownloadModalOpen={setDownloadModalOpen}
              setShareModalOpen={setShareModalOpen}
              setReportModalOpen={setReportModalOpen}
              setModalOpen={setModalOpen}
              setEditModalOpen={setEditModalOpen}
              onClick={undefined}
              mobileView={false}
              onDownloadClick={async () => {
                const downloadLink = clip.cf?.downloadUrl
                  ? clip.cf?.downloadUrl
                  : `${process.env.REACT_APP_S3_CLIP_BUCKET}/${clip.clipLink}`;
                let data;
                try {
                  data = await checkFeatureAccessQuery({
                    variables: { feature: "FREE_ENGAGED_DOWNLOAD" },
                  });
                } catch (e) {}

                const paywalledUser = data?.data?.checkFeatureAccess;

                if (paywalledUser) {
                  return setAllstarModalState({
                    isOpen: ModalType.Paywall,
                    data: { type: EPaywallType.download as EPaywallType },
                    functions: {},
                  });
                }
                trackClipDownload(clip);
                const hasMobile = Boolean(
                  clip.mobile?.find(
                    (x: any) =>
                      x.aspectRatio === "9:16" &&
                      ["Processed", "Ready"].includes(x.status),
                  ),
                );
                if (hasMobile) {
                  return setDownloadModalOpen(true);
                }
                window.open(downloadLink);
              }}
            />
          </OwnerActions>
          {!hideDivider && <Divider />}
        </Flex>
      </StyledContentColumn>
    </>
  );
};

const OwnerActions = styled(Flex)`
  max-width: 100%;
  height: 40px;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 10px;
  flex-direction: row;
`;

const StyledClipReactions = styled(ClipReactions)`
  justify-content: flex-start;
  margin-right: 10px;
`;

const StyledContentColumn = styled<any>(Flex)`
  flex-direction: column;
  width: 1155px;
  max-width: 100%;
  height: 100%;
  margin-left: 0px;
  padding: 0px;
  z-index: 2;
`;

const OwnerLink = styled(Link)``;

const Owner = styled(Flex)`
  display: flex;
  align-items: center;
  :hover ${OwnerLink} {
    color: ${(props) => props.theme.colors.envy};
  }
`;
